import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { getData, putDataWithBody, postDataWithFiles } from "../../../Services/Services";
import {
  createVehicle, getAllVehicles, updateVehicle, deleteVehicle,
  otaGetDevices,
} from "../../../Services/EndPoints";
import useModulePermissions, { FLEET_PERMISSION_IDS } from "../Common/hooks/useModulePermissions";

// ─── Constants ────────────────────────────────────────────────────────────────
const VEHICLE_TYPES = ["Car", "Bus", "Truck", "Bike", "Ambulance"];
const BRANDS = ["Tata", "Mahindra", "Maruti Suzuki", "Hyundai", "Toyota", "Honda", "Ashok Leyland", "Force Motors", "Isuzu", "Eicher", "Bajaj", "Hero", "TVS", "Other"];
const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid", "LPG"];
const COLORS = ["White", "Black", "Silver", "Grey", "Red", "Blue", "Yellow", "Green", "Orange", "Brown"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1999 }, (_, i) => currentYear - i);

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;  // 5MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Only newly-picked items hold a local blob: URL that needs releasing — existing
// (already-uploaded) items just carry a remote URL and must be left alone.
const revokeNewMediaUrls = (items) => {
  items.forEach((item) => { if (!item.isExisting && item.url) URL.revokeObjectURL(item.url); });
};

// Backend stores images and videos together in a single `image_paths` list —
// the only way to tell them apart is the file extension on the URL.
const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "webm", "mkv", "m4v", "3gp"];
const guessMediaTypeFromUrl = (url) => {
  const ext = (url.split(".").pop() || "").split(/[?#]/)[0].toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext) ? "video" : "image";
};

const EMPTY_FORM = {
  vehicleNumber: "",
  vehicleType: "",
  brand: "",
  model: "",
  yearOfManufacture: "",
  color: "",
  fuelType: "",
  seatingCapacity: "",
  engineNumber: "",
  chassisNumber: "",
  imeiNumber: "",
  deviceId: "",
  rcNumber: "",
  insuranceProvider: "",
  insuranceNumber: "",
  insuranceExpiry: "",
  fitnessExpiry: "",
  pucExpiry: "",
  driverName: "",
  licenseNumber: "",
  licenseExpiry: "",
  driverPhone: "",
  driverEmail: "",
  driverAddress: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const sectionCard = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  marginBottom: 20,
  overflow: "hidden",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};
const sectionHeader = (color1, color2) => ({
  background: `linear-gradient(135deg, ${color1}, ${color2})`,
  padding: "12px 18px",
  display: "flex",
  alignItems: "center",
  gap: 10,
});
const sectionBody = { padding: "18px 20px" };
const fieldGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "14px 20px" };
const label = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5, display: "block" };
const inputStyle = {
  width: "100%", border: "1px solid #e2e8f0", borderRadius: 7,
  padding: "8px 11px", fontSize: 13, color: "#1e293b", outline: "none",
  background: "#f8fafc", boxSizing: "border-box",
  transition: "border-color 0.15s",
};
const requiredStar = { color: "#ef4444", marginLeft: 2 };

// ─── Sub-components ───────────────────────────────────────────────────────────
const Field = ({ label: lbl, required, error, children }) => (
  <div>
    <label style={label}>{lbl}{required && <span style={requiredStar}>*</span>}</label>
    {children}
    {error && (
      <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        {error}
      </div>
    )}
  </div>
);

// Letters separated by single space / . / ' / - (or ". "), 2–60 chars, starting with a letter
const isValidName = (name) => {
  const n = name.trim();
  return n.length >= 2 && n.length <= 60 && /^[a-zA-Z]+(?:(?:\. |[ .'-])[a-zA-Z]+)*\.?$/.test(n);
};

const Input = ({ value, onChange, placeholder, type = "text", readOnly, error, maxLength, inputMode }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
    maxLength={maxLength}
    inputMode={inputMode}
    style={{
      ...inputStyle,
      background: readOnly ? "#f1f5f9" : "#f8fafc",
      cursor: readOnly ? "not-allowed" : "text",
      borderColor: error ? "#ef4444" : "#e2e8f0",
    }}
    onFocus={e => !readOnly && (e.target.style.borderColor = error ? "#ef4444" : "#1a7a73")}
    onBlur={e => (e.target.style.borderColor = error ? "#ef4444" : "#e2e8f0")}
  />
);

const MediaGrid = ({ items, onAddFiles, onRemoveItem, error }) => {
  const [previewItem, setPreviewItem] = useState(null);

  return (
  <div>
    <label
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 6, padding: "18px 14px", borderRadius: 9, cursor: "pointer",
        border: `1.5px dashed ${error ? "#ef4444" : "#cbd5e1"}`, background: "#f8fafc",
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>Click to upload images or videos</span>
      <span style={{ fontSize: 10.5, color: "#94a3b8" }}>Multiple files allowed · Image up to 5MB · Video up to 50MB</span>
      <input
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={onAddFiles}
        style={{ display: "none" }}
      />
    </label>

    {items.length > 0 && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 10, marginTop: 10 }}>
        {items.map((item) => (
          <div key={item.id} style={{ position: "relative", minWidth: 0, borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0", background: "#e2e8f0" }}>
            {item.mediaType === "image" ? (
              <img
                src={item.url} alt={item.name}
                onClick={() => setPreviewItem(item)}
                style={{ width: "100%", height: 80, objectFit: "cover", display: "block", cursor: "pointer" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <video
                src={item.url} preload="metadata" muted playsInline
                onClick={() => setPreviewItem(item)}
                style={{ width: "100%", height: 80, objectFit: "cover", display: "block", background: "#0f172a", cursor: "pointer" }}
              />
            )}
            {item.mediaType === "video" && (
              <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 24, height: 24, borderRadius: "50%", background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="white"><polygon points="6 4 20 12 6 20"/></svg>
              </span>
            )}
            {item.isExisting && (
              <span style={{ position: "absolute", top: 4, left: 4, background: "rgba(15,74,69,0.85)", color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, textTransform: "uppercase" }}>
                Saved
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemoveItem(item.id)}
              title="Remove"
              style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", border: "none", background: "rgba(239,68,68,0.9)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div style={{ fontSize: 9, color: "#64748b", background: "#f1f5f9", padding: "3px 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {item.name}{item.size != null ? ` · ${formatFileSize(item.size)}` : ""}
            </div>
          </div>
        ))}
      </div>
    )}

    {error && (
      <div style={{ color: "#ef4444", fontSize: 11, marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        {error}
      </div>
    )}

    {previewItem && (
      <div
        onClick={() => setPreviewItem(null)}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      >
        <button
          type="button"
          onClick={() => setPreviewItem(null)}
          title="Close"
          style={{ position: "absolute", top: 20, right: 20, width: 36, height: 36, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        {previewItem.mediaType === "image" ? (
          <img
            src={previewItem.url} alt={previewItem.name}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
          />
        ) : (
          <video
            src={previewItem.url} controls autoPlay
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
          />
        )}
      </div>
    )}
  </div>
  );
};

const Select = ({ value, onChange, children, error }) => (
  <select
    value={value}
    onChange={onChange}
    style={{ ...inputStyle, cursor: "pointer", borderColor: error ? "#ef4444" : "#e2e8f0" }}
    onFocus={e => (e.target.style.borderColor = error ? "#ef4444" : "#1a7a73")}
    onBlur={e => (e.target.style.borderColor = error ? "#ef4444" : "#e2e8f0")}
  >
    {children}
  </select>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CreateVehicle = () => {
  // Role's Edit / Delete rights for Vehicle Management (web_permissions from getUserProfile)
  const { canEdit, canDelete } = useModulePermissions(FLEET_PERMISSION_IDS.VEHICLE_MANAGEMENT);
  const [form, setForm] = useState(EMPTY_FORM);
  const [vehicles, setVehicles] = useState([]);
  const [otaDevices, setOtaDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeTab, setActiveTab] = useState("form");
  const [searchText, setSearchText] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [errors, setErrors] = useState({});

  // Vehicle images/videos — existing (already-uploaded, isExisting:true, url = remote URL)
  // and newly picked (isExisting:false, url = local object URL, file = raw File) items
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaError, setMediaError] = useState("");
  const mediaItemsRef = useRef(mediaItems);
  useEffect(() => { mediaItemsRef.current = mediaItems; }, [mediaItems]);
  useEffect(() => () => revokeNewMediaUrls(mediaItemsRef.current), []); // revoke on unmount

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Like `set`, but strips disallowed characters and caps length (covers paste too)
  const setClean = (field, pattern, maxLen, upper = false) => (e) => {
    let value = e.target.value.replace(pattern, "");
    if (upper) value = value.toUpperCase();
    set(field)({ target: { value: value.slice(0, maxLen) } });
  };

  // Person names: letters with single separators (space . ' -), must start with a letter.
  // ". " is kept together so initials like "K. Ravi" still work.
  const setName = (field) => (e) => {
    const value = e.target.value
      .replace(/[^a-zA-Z .'-]/g, "")
      .replace(/^[ .'-]+/, "")
      .replace(/[ .'-]{2,}/g, (run) => (run.startsWith(". ") ? ". " : run[0]));
    set(field)({ target: { value: value.slice(0, 60) } });
  };

  const handleMediaFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const accepted = [];
    const rejections = [];

    files.forEach((file) => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      if (!isImage && !isVideo) {
        rejections.push(`${file.name}: only image or video files are allowed`);
        return;
      }
      if (isImage && file.size > MAX_IMAGE_SIZE) {
        rejections.push(`${file.name}: image exceeds 5MB`);
        return;
      }
      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        rejections.push(`${file.name}: video exceeds 50MB`);
        return;
      }
      accepted.push({
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        isExisting: false,
        mediaType: isImage ? "image" : "video",
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      });
    });

    setMediaError(rejections.length > 0 ? rejections.join(" · ") : "");
    if (accepted.length > 0) setMediaItems((prev) => [...prev, ...accepted]);
  };

  const handleRemoveMediaItem = (id) => {
    setMediaItems((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target && !target.isExisting) URL.revokeObjectURL(target.url);
      return prev.filter((i) => i.id !== id);
    });
  };

  const clearAllMedia = () => {
    setMediaItems((prev) => { revokeNewMediaUrls(prev); return []; });
    setMediaError("");
  };

  const fetchVehicles = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await getData(getAllVehicles);
      // backend returns array directly
      if (Array.isArray(res)) setVehicles(res);
      else if (res?.status === "success") setVehicles(res.data || []);
      else setVehicles([]);
    } catch {
      setVehicles([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "list") fetchVehicles();
  }, [activeTab, fetchVehicles]);

  useEffect(() => {
    getData(otaGetDevices).then((res) => {
      if (Array.isArray(res)) setOtaDevices(res);
    }).catch(() => {});
  }, []);

  const validate = () => {
    const errs = {};
    const today = new Date(); today.setHours(0, 0, 0, 0);

    // ── Vehicle Details ──
    if (!form.vehicleNumber.trim())
      errs.vehicleNumber = "Vehicle number is required";
    else if (!/^[A-Z0-9\s-]{4,15}$/i.test(form.vehicleNumber.trim()))
      errs.vehicleNumber = "Enter a valid vehicle number (e.g. TS19 GS4321)";

    if (!form.vehicleType)
      errs.vehicleType = "Vehicle type is required";

    if (!form.fuelType)
      errs.fuelType = "Fuel type is required";

    if (form.seatingCapacity && (isNaN(form.seatingCapacity) || Number(form.seatingCapacity) < 1 || Number(form.seatingCapacity) > 99))
      errs.seatingCapacity = "Enter a valid seating capacity (1–99)";

    // IMEI & Device ID — required only on create
    if (!editId) {
      if (!form.imeiNumber.trim())
        errs.imeiNumber = "IMEI number is required";
      else if (!/^\d{15}$/.test(form.imeiNumber.trim()))
        errs.imeiNumber = "IMEI must be exactly 15 digits";

      if (!form.deviceId.trim())
        errs.deviceId = "Device ID is required";
    }

    // ── Registration & Compliance ──
    if (!form.rcNumber.trim())
      errs.rcNumber = "RC number is required";

    if (!form.insuranceNumber.trim())
      errs.insuranceNumber = "Insurance number is required";

    if (!form.insuranceExpiry)
      errs.insuranceExpiry = "Insurance expiry date is required";
    else if (new Date(form.insuranceExpiry) < today)
      errs.insuranceExpiry = "Insurance policy has already expired";

    if (form.fitnessExpiry && new Date(form.fitnessExpiry) < today)
      errs.fitnessExpiry = "Fitness certificate has already expired";

    if (form.pucExpiry && new Date(form.pucExpiry) < today)
      errs.pucExpiry = "PUC certificate has already expired";

    // ── Driver Details ──
    if (!form.driverName.trim())
      errs.driverName = "Driver name is required";
    else if (!isValidName(form.driverName))
      errs.driverName = "Name should contain only letters";

    if (!form.licenseNumber.trim())
      errs.licenseNumber = "License number is required";

    if (!form.licenseExpiry)
      errs.licenseExpiry = "License expiry date is required";
    else if (new Date(form.licenseExpiry) < today)
      errs.licenseExpiry = "Driver license has already expired";

    if (!form.driverPhone.trim())
      errs.driverPhone = "Phone number is required";
    else if (!/^\d{10}$/.test(form.driverPhone.trim()))
      errs.driverPhone = "Enter a valid 10-digit phone number";

    if (form.driverEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.driverEmail.trim()))
      errs.driverEmail = "Enter a valid email address";

    if (form.emergencyContactName.trim() && !isValidName(form.emergencyContactName))
      errs.emergencyContactName = "Name should contain only letters";

    if (form.emergencyContactPhone && !/^\d{10}$/.test(form.emergencyContactPhone.trim()))
      errs.emergencyContactPhone = "Enter a valid 10-digit phone number";

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the highlighted errors before submitting");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        vehicleNumber: form.vehicleNumber.toUpperCase().trim(),
        seatingCapacity: form.seatingCapacity ? Number(form.seatingCapacity) : undefined,
      };
      const newFiles = mediaItems.filter((m) => !m.isExisting).map((m) => m.file);
      const formData = new FormData();
      let res;

      if (editId) {
        formData.append("data", JSON.stringify({ ...payload, id: editId }));

        // Backend expects every kept item's raw bytes re-sent via `files`, not just
        // referenced by URL — re-fetch already-uploaded media so it round-trips as binary too
        const existingItems = mediaItems.filter((m) => m.isExisting);
        for (const item of existingItems) {
          try {
            const blob = await (await fetch(item.url)).blob();
            formData.append("files", blob, item.name);
          } catch {
            toast.error(`Could not re-attach "${item.name}" — it may be dropped from this update`);
          }
        }
        newFiles.forEach((file) => formData.append("files", file));
        // PUT /vehicleinfo/update — multipart form-data (data + files); id goes in `data`, not URL
        res = await putDataWithBody(updateVehicle, formData, {});
      } else {
        formData.append("data", JSON.stringify(payload));
        newFiles.forEach((file) => formData.append("files", file));
        // POST /vehicleinfo/create — multipart form-data (data + files)
        res = await postDataWithFiles(createVehicle, formData);
      }

      if (res?.status === "success") {
        toast.success(res.msg || (editId ? "Vehicle updated successfully" : "Vehicle created successfully"));
        setForm(EMPTY_FORM);
        setEditId(null);
        clearAllMedia();
        setActiveTab("list");
      } else {
        toast.error(res?.msg || "Failed to save vehicle");
      }
    } catch {
      toast.error("An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (v) => {
    setForm({
      vehicleNumber:        v.vehicle_number         || "",
      vehicleType:          v.vehicle_type           || "",
      brand:                v.brand                  || "",
      model:                v.model                  || "",
      yearOfManufacture:    v.year_of_manufacture    || "",
      color:                v.color                  || "",
      fuelType:             v.fuel_type              || "",
      seatingCapacity:      v.seating_capacity       || "",
      engineNumber:         v.engine_number          || "",
      chassisNumber:        v.chassis_number         || "",
      imeiNumber:           v.imei_number            || "",
      deviceId:             v.device_id              || "",
      rcNumber:             v.rc_number              || "",
      insuranceProvider:    v.insurance_provider     || "",
      insuranceNumber:      v.insurance_number       || "",
      insuranceExpiry:      v.insurance_expiry       || "",
      fitnessExpiry:        v.fitness_expiry         || "",
      pucExpiry:            v.puc_expiry             || "",
      driverName:           v.driver_name            || "",
      licenseNumber:        v.license_number         || "",
      licenseExpiry:        v.license_expiry         || "",
      driverPhone:          v.driver_phone           || "",
      driverEmail:          v.driver_email           || "",
      driverAddress:        v.driver_address         || "",
      emergencyContactName: v.emergency_contact_name  || "",
      emergencyContactPhone:v.emergency_contact_phone || "",
    });
    setEditId(v.id);

    // Populate previews for already-uploaded media — backend returns images and
    // videos together in one `image_paths` list, typed only by file extension
    revokeNewMediaUrls(mediaItems); // drop any unsaved local previews left from a prior edit session
    const existingMedia = v.image_paths || [];
    setMediaItems(existingMedia.map((url, i) => {
      let name = url.split("/").pop() || url;
      try { name = decodeURIComponent(name); } catch { /* keep raw name */ }
      return { id: `existing-${v.id}-${i}`, isExisting: true, mediaType: guessMediaTypeFromUrl(url), url, name };
    }));
    setMediaError("");

    setActiveTab("form");
  };

  const handleDelete = async (id) => {
    try {
      // PUT /vehicleinfo/delete — soft delete, id in body
      const res = await putDataWithBody(deleteVehicle, { id }, {});
      if (res?.status === "success") {
        toast.success(res.msg || "Vehicle deleted successfully");
        fetchVehicles();
      } else {
        toast.error(res?.msg || "Failed to delete vehicle");
      }
    } catch {
      toast.error("Error deleting vehicle");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleReset = () => { setForm(EMPTY_FORM); setEditId(null); setErrors({}); clearAllMedia(); };

  const filtered = vehicles.filter(v =>
    !searchText || v.vehicle_number?.toLowerCase().includes(searchText.toLowerCase()) ||
    v.driver_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    v.vehicle_type?.toLowerCase().includes(searchText.toLowerCase())
  );

  // ── Tabs ──
  const tabBtn = (key, label) => (
    <button
      onClick={() => setActiveTab(key)}
      style={{
        padding: "9px 22px", borderRadius: 8, border: "none", cursor: "pointer",
        fontSize: 13, fontWeight: 600,
        background: activeTab === key ? "linear-gradient(135deg, #0f4a45, #1a7a73)" : "#f1f5f9",
        color: activeTab === key ? "#fff" : "#64748b",
        boxShadow: activeTab === key ? "0 2px 8px rgba(26,122,115,0.3)" : "none",
        transition: "all 0.15s",
      }}
    >{label}</button>
  );

  return (
    <div style={{ padding: "16px 20px", fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center" }}>
        {tabBtn("form", editId ? "✏ Edit Vehicle" : "+ Create Vehicle")}
        {tabBtn("list", "Vehicle List")}
        {editId && (
          <button
            onClick={handleReset}
            title="Discard edit and create a new vehicle"
            style={{
              padding: "9px 18px", borderRadius: 8, border: "1.5px dashed #0f4a45",
              background: "#f0fdfb", color: "#0f4a45", fontSize: 13, fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              marginLeft: 4,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Vehicle
          </button>
        )}
      </div>

      {/* ── FORM TAB ── */}
      {activeTab === "form" && (
        <div>
          {/* Vehicle Details */}
          <div style={sectionCard}>
            <div style={sectionHeader("#0f4a45", "#1a7a73")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
              </svg>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Vehicle Details</span>
            </div>
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="Vehicle Number" required error={errors.vehicleNumber}>
                  <Input value={form.vehicleNumber} onChange={set("vehicleNumber")} placeholder="e.g. TS19 GS4321" readOnly={!!editId} error={errors.vehicleNumber} />
                </Field>
                <Field label="Vehicle Type" required error={errors.vehicleType}>
                  <Select value={form.vehicleType} onChange={set("vehicleType")} error={errors.vehicleType}>
                    <option value="">Select type</option>
                    {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </Field>
                <Field label="Brand">
                  <Select value={form.brand} onChange={set("brand")}>
                    <option value="">Select brand</option>
                    {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                  </Select>
                </Field>
                <Field label="Model">
                  <Input value={form.model} onChange={set("model")} placeholder="e.g. Winger, Ace" />
                </Field>
                <Field label="Year of Manufacture">
                  <Select value={form.yearOfManufacture} onChange={set("yearOfManufacture")}>
                    <option value="">Select year</option>
                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                  </Select>
                </Field>
                <Field label="Color">
                  <Select value={form.color} onChange={set("color")}>
                    <option value="">Select color</option>
                    {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label="Fuel Type" required error={errors.fuelType}>
                  <Select value={form.fuelType} onChange={set("fuelType")} error={errors.fuelType}>
                    <option value="">Select fuel type</option>
                    {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                  </Select>
                </Field>
                <Field label="Seating Capacity" error={errors.seatingCapacity}>
                  <Input inputMode="numeric" maxLength={2} value={form.seatingCapacity} onChange={setClean("seatingCapacity", /\D/g, 2)} placeholder="e.g. 12" error={errors.seatingCapacity} />
                </Field>
                <Field label="Engine Number">
                  <Input maxLength={20} value={form.engineNumber} onChange={setClean("engineNumber", /[^a-zA-Z0-9]/g, 20, true)} placeholder="Engine number" />
                </Field>
                <Field label="Chassis Number">
                  <Input maxLength={17} value={form.chassisNumber} onChange={setClean("chassisNumber", /[^a-zA-Z0-9]/g, 17, true)} placeholder="17-char chassis / VIN" />
                </Field>
                <Field label="IMEI Number" required={!editId} error={errors.imeiNumber}>
                  <Input inputMode="numeric" maxLength={15} value={form.imeiNumber} onChange={setClean("imeiNumber", /\D/g, 15)} placeholder="15-digit IMEI" readOnly={!!editId} error={errors.imeiNumber} />
                </Field>
                <Field label="Device ID" required={!editId} error={errors.deviceId}>
                  {editId ? (
                    <Input value={form.deviceId} readOnly error={errors.deviceId} />
                  ) : (
                    <Select value={form.deviceId} onChange={set("deviceId")} error={errors.deviceId}>
                      <option value="">Select device ID</option>
                      {otaDevices.map(d => (
                        <option key={d.deviceId} value={d.deviceId}>{d.deviceId}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
              <div style={{ marginTop: 14, maxWidth: 640 }}>
                <Field label="Vehicle Photos / Videos">
                  <MediaGrid
                    items={mediaItems}
                    onAddFiles={handleMediaFilesSelected}
                    onRemoveItem={handleRemoveMediaItem}
                    error={mediaError}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Registration & Compliance */}
          <div style={sectionCard}>
            <div style={sectionHeader("#0f4a45", "#145c57")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
              </svg>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Registration & Compliance</span>
            </div>
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="RC Number" required error={errors.rcNumber}>
                  <Input maxLength={15} value={form.rcNumber} onChange={setClean("rcNumber", /[^a-zA-Z0-9 ]/g, 15, true)} placeholder="Registration certificate no." error={errors.rcNumber} />
                </Field>
                <Field label="Insurance Provider">
                  <Input value={form.insuranceProvider} onChange={set("insuranceProvider")} placeholder="e.g. New India Assurance" />
                </Field>
                <Field label="Insurance Number" required error={errors.insuranceNumber}>
                  <Input maxLength={30} value={form.insuranceNumber} onChange={setClean("insuranceNumber", /[^a-zA-Z0-9/-]/g, 30, true)} placeholder="Policy number" error={errors.insuranceNumber} />
                </Field>
                <Field label="Insurance Expiry" required error={errors.insuranceExpiry}>
                  <Input type="date" value={form.insuranceExpiry} onChange={set("insuranceExpiry")} error={errors.insuranceExpiry} />
                </Field>
                <Field label="Fitness Certificate Expiry" error={errors.fitnessExpiry}>
                  <Input type="date" value={form.fitnessExpiry} onChange={set("fitnessExpiry")} error={errors.fitnessExpiry} />
                </Field>
                <Field label="PUC Expiry" error={errors.pucExpiry}>
                  <Input type="date" value={form.pucExpiry} onChange={set("pucExpiry")} error={errors.pucExpiry} />
                </Field>
              </div>
            </div>
          </div>

          {/* Driver Details */}
          <div style={sectionCard}>
            <div style={sectionHeader("#059669", "#047857")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Driver Details</span>
            </div>
            <div style={sectionBody}>
              <div style={fieldGrid}>
                <Field label="Driver Name" required error={errors.driverName}>
                  <Input maxLength={60} value={form.driverName} onChange={setName("driverName")} placeholder="Full name" error={errors.driverName} />
                </Field>
                <Field label="License Number" required error={errors.licenseNumber}>
                  <Input value={form.licenseNumber} onChange={set("licenseNumber")} placeholder="DL number" error={errors.licenseNumber} />
                </Field>
                <Field label="License Expiry" required error={errors.licenseExpiry}>
                  <Input type="date" value={form.licenseExpiry} onChange={set("licenseExpiry")} error={errors.licenseExpiry} />
                </Field>
                <Field label="Phone Number" required error={errors.driverPhone}>
                  <Input type="tel" value={form.driverPhone} onChange={set("driverPhone")} placeholder="10-digit mobile" error={errors.driverPhone} />
                </Field>
                <Field label="Email" error={errors.driverEmail}>
                  <Input type="email" value={form.driverEmail} onChange={set("driverEmail")} placeholder="driver@example.com" error={errors.driverEmail} />
                </Field>
                <Field label="Emergency Contact Name" error={errors.emergencyContactName}>
                  <Input maxLength={60} value={form.emergencyContactName} onChange={setName("emergencyContactName")} placeholder="Contact person name" error={errors.emergencyContactName} />
                </Field>
                <Field label="Emergency Contact Phone" error={errors.emergencyContactPhone}>
                  <Input type="tel" value={form.emergencyContactPhone} onChange={set("emergencyContactPhone")} placeholder="Emergency number" error={errors.emergencyContactPhone} />
                </Field>
              </div>
              <div style={{ marginTop: 14 }}>
                <Field label="Address">
                  <textarea
                    value={form.driverAddress}
                    onChange={set("driverAddress")}
                    placeholder="Driver residential address"
                    rows={3}
                    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, width: "100%" }}
                    onFocus={e => (e.target.style.borderColor = "#1a7a73")}
                    onBlur={e => (e.target.style.borderColor = "#e2e8f0")}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 4 }}>
            <button
              onClick={handleReset}
              style={{
                padding: "10px 24px", borderRadius: 8, border: "1.5px solid #e2e8f0",
                background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >Reset</button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: "10px 28px", borderRadius: 8, border: "none",
                background: loading ? "#94a3b8" : "linear-gradient(135deg, #0f4a45, #1a7a73)",
                color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: "0 2px 8px rgba(26,122,115,0.35)",
                display: "flex", alignItems: "center", gap: 7,
              }}
            >
              {loading && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/>
                </svg>
              )}
              {loading ? "Saving…" : editId ? "Update Vehicle" : "Create Vehicle"}
            </button>
          </div>
        </div>
      )}

      {/* ── LIST TAB ── */}
      {activeTab === "list" && (
        <div style={sectionCard}>
          {/* List header */}
          <div style={{ ...sectionHeader("#0f4a45", "#1a7a73"), justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Registered Vehicles</span>
              <span style={{ background: "rgba(255,255,255,0.25)", color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                {filtered.length}
              </span>
            </div>
            <input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by number, driver, type..."
              style={{ padding: "6px 12px", borderRadius: 7, border: "none", fontSize: 12, width: 240, outline: "none" }}
            />
          </div>

          <div style={{ overflowX: "auto" }}>
            {listLoading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading vehicles…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No vehicles found. <button onClick={() => setActiveTab("form")} style={{ color: "#0f4a45", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Create one</button>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    {["#", "Vehicle No.", "Type", "Brand / Model", "Fuel", "Driver", "Phone", "Status", ...(canEdit || canDelete ? ["Actions"] : [])].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px", fontSize: 11, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v, i) => (
                    <tr key={v.id || i} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.1s" }}
                      onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ background: "#f0fdfb", color: "#0f4a45", padding: "3px 9px", borderRadius: 6, fontWeight: 700 }}>{v.vehicle_number}</span>
                      </td>
                      <td style={{ padding: "10px 14px", color: "#475569" }}>{v.vehicle_type || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#475569" }}>{[v.brand, v.model].filter(Boolean).join(" / ") || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#475569" }}>{v.fuel_type || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#1e293b", fontWeight: 600 }}>{v.driver_name || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#475569" }}>{v.driver_phone || "—"}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ background: "#dcfce7", color: "#16a34a", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>Active</span>
                      </td>
                      {(canEdit || canDelete) && <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {canEdit && <button onClick={() => handleEdit(v)} title="Edit"
                            style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #ccfbf1", background: "#f0fdfb", color: "#0f4a45", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>}
                          {canDelete && <button onClick={() => setDeleteConfirm(v.id)} title="Delete"
                            style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>}
                        </div>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 380, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Delete Vehicle</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>This action cannot be undone.</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: "8px 20px", borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateVehicle;
